import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

type Leg = "departure" | "return";

const SELECT_BTN = ".fly-cardB2C__select-btn";
const CARD = ".fly-cardB2C";
const CARD_SELECTED_CLASS = "is-selected";
const BTN_SELECTED_CLASS = "fly-buttonToggleCard--selected";
const RT_BLOCK = ".fly-rtFeaturedB2C, .fly-rtDefaultB2C";
const RT_GROUP = ".fly-rtFeaturedB2C__group, .fly-rtDefaultB2C__group";
const OW_BLOCK = ".fly-owDefaultB2C";
const SUMMARY_BAR = ".fly-availability__summary-bar";
const SUMMARY_CARD = ".fly-availability__summary-card";

const LABEL_SELECT = "Seleccionar";
const LABEL_SELECTED = "Seleccionado";
const EMPTY_MESSAGE: Record<Leg, string> = {
  departure: "selecciona tu vuelo de ida",
  return: "selecciona tu vuelo de vuelta",
};

/**
 * Selección de vuelos en la disponibilidad (comportamiento de STG):
 *
 * - RT (combinaciones): seleccionar la ida o la vuelta marca la combinación
 *   completa (misma fila del grupo). Solo una combinación a la vez; volver a
 *   pulsar cualquiera de las dos la deselecciona.
 * - OW: una card por columna (ida / vuelta), independientes; volver a pulsar
 *   la deselecciona.
 * - RT y OW se excluyen: elegir en uno limpia el otro.
 * - La barra de resumen (desktop y mobile) solo se muestra con alguna
 *   selección: rellena ida/vuelta con los datos de la card o muestra
 *   "selecciona tu vuelo de…", y actualiza el precio de "Continuar".
 * - La X del resumen elimina esa selección.
 *
 * Emite `flyCardSelection:change` desde la card pulsada con
 * `{ selected: boolean }`, y `flyCardSelection:state` en `document` cada vez
 * que cambia la selección con `{ steps: [ida, vuelta] }` (booleanos), que usa
 * `fly-journey-nav` para marcar los pasos ya elegidos.
 */
export class FlyCardSelection implements Plugin {
  private readonly clickHandler = (event: MouseEvent) =>
    this.handleClick(event);

  constructor(private readonly element: HTMLElement) {}

  init(): void {
    // Se parte sin selección, como en producción
    this.getAllCards().forEach((card) => this.setCardSelected(card, false));
    this.element.addEventListener("click", this.clickHandler);
    this.renderSummary();
  }

  destroy(): void {
    this.element.removeEventListener("click", this.clickHandler);
  }

  private handleClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;

    const close = target.closest(".fly-summaryInfoflightB2C__close");
    if (close) {
      const leg = close
        .closest(SUMMARY_CARD)
        ?.getAttribute("data-summary-leg") as Leg | null;
      if (leg) this.clearLeg(leg);
      return;
    }

    const button = target.closest(SELECT_BTN);
    const card = button?.closest<HTMLElement>(CARD);
    if (!card || !this.element.contains(card)) return;

    const selected = card.closest(RT_BLOCK)
      ? this.toggleRoundTrip(card)
      : this.toggleOneWay(card);

    this.renderSummary();
    card.dispatchEvent(
      new CustomEvent("flyCardSelection:change", {
        bubbles: true,
        detail: { selected },
      }),
    );
  }

  private toggleRoundTrip(card: HTMLElement): boolean {
    const pair = this.getRoundTripPair(card);
    const wasSelected = card.classList.contains(CARD_SELECTED_CLASS);

    this.clearAll();
    if (!wasSelected) pair.forEach((c) => this.setCardSelected(c, true));
    return !wasSelected;
  }

  private toggleOneWay(card: HTMLElement): boolean {
    const wasSelected = card.classList.contains(CARD_SELECTED_CLASS);
    const leg = this.getLeg(card);

    // Elegir en OW limpia cualquier combinación RT
    this.getRoundTripCards().forEach((c) => this.setCardSelected(c, false));
    this.getOneWayCards(leg).forEach((c) => this.setCardSelected(c, false));
    if (!wasSelected) this.setCardSelected(card, true);
    return !wasSelected;
  }

  private clearLeg(leg: Leg): void {
    const rtSelected = this.getRoundTripCards().some((c) =>
      c.classList.contains(CARD_SELECTED_CLASS),
    );
    if (rtSelected) {
      // Una combinación no se puede quedar a medias
      this.getRoundTripCards().forEach((c) => this.setCardSelected(c, false));
    } else {
      this.getOneWayCards(leg).forEach((c) => this.setCardSelected(c, false));
    }
    this.renderSummary();
  }

  private clearAll(): void {
    this.getAllCards().forEach((c) => this.setCardSelected(c, false));
  }

  /** Card de ida + card de vuelta de la misma fila del grupo RT. */
  private getRoundTripPair(card: HTMLElement): HTMLElement[] {
    const group = card.closest(RT_GROUP) ?? card.closest(RT_BLOCK);
    if (!group) return [card];

    const departures = this.getColumnCards(group, "departure");
    const returns = this.getColumnCards(group, "return");
    const ownColumn = this.getLeg(card) === "departure" ? departures : returns;
    const row = Math.max(0, ownColumn.indexOf(card));

    return [
      departures[Math.min(row, departures.length - 1)],
      returns[Math.min(row, returns.length - 1)],
    ].filter(Boolean);
  }

  private getColumnCards(scope: Element, leg: Leg): HTMLElement[] {
    return Array.from(scope.querySelectorAll<HTMLElement>(CARD)).filter(
      (c) => this.getLeg(c) === leg,
    );
  }

  private getLeg(card: HTMLElement): Leg {
    return card.closest(
      ".fly-gridRTB2C__column--departure, .fly-gridOWB2C__column--start",
    )
      ? "departure"
      : "return";
  }

  private getAllCards(): HTMLElement[] {
    return Array.from(
      this.element.querySelectorAll<HTMLElement>(
        `${RT_BLOCK.split(", ")
          .map((b) => `${b} ${CARD}`)
          .join(", ")}, ${OW_BLOCK} ${CARD}`,
      ),
    );
  }

  private getRoundTripCards(): HTMLElement[] {
    return this.getAllCards().filter((c) => c.closest(RT_BLOCK));
  }

  private getOneWayCards(leg: Leg): HTMLElement[] {
    return this.getAllCards().filter(
      (c) => c.closest(OW_BLOCK) && this.getLeg(c) === leg,
    );
  }

  private setCardSelected(card: HTMLElement, selected: boolean): void {
    card.classList.toggle(CARD_SELECTED_CLASS, selected);
    const button = card.querySelector<HTMLElement>(SELECT_BTN);
    if (!button) return;
    button.classList.toggle(BTN_SELECTED_CLASS, selected);
    button.setAttribute("aria-pressed", String(selected));
    const label = button.querySelector(".fly-buttonToggleCard__label");
    if (label) label.textContent = selected ? LABEL_SELECTED : LABEL_SELECT;
  }

  private getSelected(leg: Leg): HTMLElement | null {
    return (
      this.getAllCards().find(
        (c) =>
          c.classList.contains(CARD_SELECTED_CLASS) && this.getLeg(c) === leg,
      ) ?? null
    );
  }

  // --- Barra de resumen -------------------------------------------------

  private renderSummary(): void {
    const departure = this.getSelected("departure");
    const ret = this.getSelected("return");
    const hasSelection = Boolean(departure || ret);
    const bars = this.element.querySelectorAll<HTMLElement>(SUMMARY_BAR);
    const visibilityChanged = Array.from(bars).some(
      (bar) => bar.hidden === hasSelection,
    );

    bars.forEach((bar) => {
      bar.hidden = !hasSelection;
      this.renderLeg(bar, "departure", departure);
      this.renderLeg(bar, "return", ret);
      const price = bar.querySelector(".core-button__price-current");
      if (price) price.textContent = this.formatPrice(departure, ret);
    });

    // flyAvailabilityLayout recalcula sus modos sticky al mostrar/ocultar
    // el resumen (escucha resize).
    if (visibilityChanged) window.dispatchEvent(new Event("resize"));

    document.dispatchEvent(
      new CustomEvent("flyCardSelection:state", {
        detail: { steps: [Boolean(departure), Boolean(ret)] },
      }),
    );
  }

  private renderLeg(
    bar: HTMLElement,
    leg: Leg,
    card: HTMLElement | null,
  ): void {
    const filled = bar.querySelector<HTMLElement>(
      `${SUMMARY_CARD}[data-summary-leg="${leg}"][data-summary-state="filled"]`,
    );
    const empty = bar.querySelector<HTMLElement>(
      `${SUMMARY_CARD}[data-summary-leg="${leg}"][data-summary-state="empty"]`,
    );
    if (filled) filled.hidden = !card;
    if (empty) {
      empty.hidden = Boolean(card);
      const message = empty.querySelector(".fly-infoCardSummaryB2C p");
      if (message) message.textContent = EMPTY_MESSAGE[leg];
    }
    if (card && filled) this.fillSummaryCard(filled, card);
  }

  private fillSummaryCard(target: HTMLElement, card: HTMLElement): void {
    const text = (root: Element, selector: string) =>
      root.querySelector(selector)?.textContent?.trim() ?? "";
    const set = (selector: string, value: string) => {
      const el = target.querySelector(selector);
      if (el) el.textContent = value;
    };

    const date = this.getLegDate(card);
    const from = ".fly-cardB2C-timetable--align-left";
    const to = ".fly-cardB2C-timetable--align-right";

    set(
      `${from} .fly-cardB2C-timetable__time`,
      text(
        card,
        ".fly-cardB2C__timetable--departure .fly-cardB2C-timetable__time",
      ),
    );
    set(
      `${from} .fly-cardB2C-timetable__location`,
      text(
        card,
        ".fly-cardB2C__timetable--departure .fly-cardB2C-timetable__location",
      ),
    );
    set(`${from} .fly-cardB2C-timetable__date`, date);
    set(
      `${to} .fly-cardB2C-timetable__time`,
      text(
        card,
        ".fly-cardB2C__timetable--arrival .fly-cardB2C-timetable__time",
      ),
    );
    set(
      `${to} .fly-cardB2C-timetable__location`,
      text(
        card,
        ".fly-cardB2C__timetable--arrival .fly-cardB2C-timetable__location",
      ),
    );
    set(`${to} .fly-cardB2C-timetable__date`, date);
    set(
      ".fly-cardB2C-timeflight__eta",
      text(card, ".fly-cardB2C-timeflight__eta"),
    );
    set(
      ".fly-cardB2C-timeflight__stopover",
      text(card, ".fly-cardB2C-timeflight__stopover"),
    );

    const logo = card.querySelector<HTMLImageElement>(
      ".fly-cardB2C-airline__slot img",
    );
    const targetLogo = target.querySelector<HTMLImageElement>(
      ".fly-cardB2C-airline__slot img",
    );
    if (logo && targetLogo) {
      targetLogo.src = logo.src;
      targetLogo.alt = logo.alt;
    }
  }

  /** Fecha del trayecto ("12 abr 2026" → "12 abr") tomada del contexto Ida/Vuelta del bloque. */
  private getLegDate(card: HTMLElement): string {
    const leg = this.getLeg(card);
    const contextClass =
      leg === "departure"
        ? ".fly-journeyContextB2C--departure"
        : ".fly-journeyContextB2C--arrival";

    let raw = "";
    const owColumn = card.closest(
      ".fly-gridOWB2C__column--start, .fly-gridOWB2C__column--end",
    );
    if (owColumn) {
      raw =
        owColumn.querySelector(".fly-journeyContextB2C__date")?.textContent ??
        "";
    } else {
      raw =
        card
          .closest(RT_BLOCK)
          ?.querySelector(`${contextClass} .fly-journeyContextB2C__date`)
          ?.textContent ?? "";
    }

    const match = raw.trim().match(/^(\d{1,2}\s+\S+)/);
    return match ? match[1] : "";
  }

  private formatPrice(
    departure: HTMLElement | null,
    ret: HTMLElement | null,
  ): string {
    const parse = (value: string | null | undefined) => {
      const n = Number(
        (value ?? "")
          .replace(/[^\d,.-]/g, "")
          .replace(/\.(?=\d{3}\b)/g, "")
          .replace(",", "."),
      );
      return Number.isFinite(n) ? n : 0;
    };

    let total = 0;
    const rtCard = [departure, ret].find((c) => c?.closest(RT_BLOCK));
    if (rtCard) {
      // RT: precio de la combinación (sticky de tarifa del grupo)
      const group = rtCard.closest(RT_GROUP) ?? rtCard.closest(RT_BLOCK);
      total = parse(
        group?.querySelector(".fly-stickyInfoB2C__price-amount")?.textContent,
      );
    } else {
      [departure, ret].forEach((c) => {
        total += parse(c?.querySelector(".fly-cardB2C__price")?.textContent);
      });
    }

    return `${total.toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} €`;
  }
}

PluginRegistry.register("fly-card-selection", FlyCardSelection);
